import * as React from 'react';
import { cn } from '@common/bw-ui/lib/utils';
import {
  AlertCircleSolidAnimatedIcon,
  EyeIcon,
  EyeOffIcon,
  SuccessCircleAnimatedIcon,
} from '@common/bw-ui/icons';
import { useFormControl } from './form-field';
import { Input, type InputProps } from './input';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /** Validated state: green field styling + a check icon beside the eye toggle */
  success?: boolean;
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, variant = 'form', success, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);
    // Invalid comes from an explicit aria-invalid or the enclosing FormField's
    // error; it takes precedence over success (the Input styles off the same
    // attributes, so the two must never be set together)
    const invalid = Boolean(useFormControl(props)['aria-invalid']);
    const showSuccess = Boolean(success) && !invalid;
    return (
      <div data-slot="password-input" className="tw:relative tw:w-full">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          variant={variant}
          data-success={showSuccess ? '' : undefined}
          className={cn(
            'tw:pr-11',
            (showSuccess || invalid) && 'tw:pr-16',
            className,
          )}
          {...props}
        />
        {showSuccess && (
          <SuccessCircleAnimatedIcon
            data-slot="success-icon"
            className="tw:absolute tw:right-9 tw:top-1/2 tw:size-5 tw:-translate-y-1/2 tw:text-success"
            aria-hidden="true"
          />
        )}
        {invalid && (
          <AlertCircleSolidAnimatedIcon
            data-slot="error-icon"
            className="tw:absolute tw:right-9 tw:top-1/2 tw:size-5 tw:-translate-y-1/2 tw:text-destructive"
            aria-hidden="true"
          />
        )}
        <button
          type="button"
          aria-label={visible ? 'Hide password' : 'Show password'}
          onClick={() => setVisible((v) => !v)}
          className="tw:absolute tw:right-2.5 tw:top-1/2 tw:flex tw:-translate-y-1/2 tw:cursor-pointer tw:items-center tw:justify-center tw:border-0 tw:bg-transparent tw:p-0 tw:text-foreground tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring"
        >
          {visible ? (
            <EyeIcon className="tw:size-5" aria-hidden="true" />
          ) : (
            <EyeOffIcon className="tw:size-5" aria-hidden="true" />
          )}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
